import { deSteegStyleVertical } from "pages/pricelist-styles/DeSteegStyleVertical";
import { missouriStyleVertical } from "pages/pricelist-styles/MissouriStyleVertical";
import { useContext, useMemo } from "react";

import { UserContext } from "../App";
import { deSteegStyle } from "../pages/pricelist-styles/DeSteegStyle";
import { deSteegStyleMultiScreen } from "../pages/pricelist-styles/DeSteegStyleMultiScreen";
import { easyGoingStyle } from "../pages/pricelist-styles/EasyGoingStyle";
import { easyGoingStyleHoreca } from "../pages/pricelist-styles/EasyGoingStyleHoreca";
import { easyGoingStyleMultiScreen } from "../pages/pricelist-styles/EasyGoingStyleMultiScreen";
import { easyGoingStyleVertical } from "../pages/pricelist-styles/EasyGoingStyleVertical";
import { maryJaneStyle } from "../pages/pricelist-styles/MaryJaneStyle";
import { maryJaneStyleMultiScreen } from "../pages/pricelist-styles/MaryJaneStyleMultiScreen";
import { maryJaneStyleVertical } from "../pages/pricelist-styles/MaryJaneStyleVertical";
import { mississippiStyle } from "../pages/pricelist-styles/MississippiStyle";
import { mississippiStyleMultiScreen } from "../pages/pricelist-styles/MississippiStyleMultiScreen";
import { mississippiStyleVertical } from "../pages/pricelist-styles/MississippiStyleVertical";
import { missouriStyle } from "../pages/pricelist-styles/MissouriStyle";
import { missouriStyleMultiScreen } from "../pages/pricelist-styles/MissouriStyleMultiScreen";
import { roundabout69Style } from "../pages/pricelist-styles/RoundAbout69Style";
import { roundabout69StyleLight } from "../pages/pricelist-styles/RoundAbout69StyleLight";
import { roundAbout69StyleLightMultiScreen } from "../pages/pricelist-styles/RoundAbout69StyleLightMultiScreen";
import { slowMotionStyle } from "../pages/pricelist-styles/SlowMotionStyle";
import { ShopContext } from "./Shop";
import { JsonPrice, Price, PriceForTable, ScreenExtraConfig } from "./types";

export const isPortrait = window.location.toString().includes("portrait");

export const useCurrentShopGroup = () => {
    const user = useContext(UserContext);
    const shopContext = useContext(ShopContext);

    const currentShopGroup = user.profile.shop_groups.find((sg) =>
        sg.shops.some((shop) => shop.id === shopContext.shopId)
    );

    return currentShopGroup ?? { id: "", name: "" };
};

export const extractPrices = (sourceArray: JsonPrice[], pricesForTable: PriceForTable, category: string) => {
    if (sourceArray?.length) {
        for (const item of sourceArray) {
            const label = item.label.toString().replace(" ", "").replace("gram", "g");
            pricesForTable.prices[label] = item.price;
        }
    }
};

export const sortByLabelSuffix = (items: JsonPrice[], labelOrder: string[]): JsonPrice[] => {
    const getSuffixIndex = (label: string): number => {
        const suffix = label.replace(/^\d+(\.\d+)?/, ""); // remove numeric prefix
        const index = labelOrder.indexOf(suffix);
        return index === -1 ? labelOrder.length : index; // unknown suffix goes to the end
    };

    return [...items].sort((a, b) => {
        const indexA = getSuffixIndex(a.label);
        const indexB = getSuffixIndex(b.label);
        return indexA - indexB;
    });
};

export const filterDeactivatedJsonPrices = (prices: Price[]): Price[] => {
    return prices.map((price) => ({
        ...price,
        json_prices:
            price.json_prices?.filter((json_price) => !price.deactivated_labels?.includes(json_price.label)) || null,
    }));
};

export const getShopStyle = ({
    shopId,
    orientation,
    screenId,
    pathname = window.location.pathname,
    extraConfig = {},
}: {
    shopId: string;
    orientation?: string;
    screenId?: string;
    pathname?: string;
    extraConfig?: ScreenExtraConfig;
}) => {
    switch (shopId) {
        case "bbc0cfa7-e230-4338-817a-55190debdac0": // Mary Jane
            if (screenId) return maryJaneStyleMultiScreen;
            return orientation === "portrait" ? maryJaneStyleVertical : maryJaneStyle;

        case "a08a13e2-a31b-4b6d-b2b4-0491cb3d2227": // Missouri
            if (screenId) return missouriStyleMultiScreen;
            return orientation === "portrait" ? missouriStyleVertical : missouriStyle;

        case "ddd70853-5e9d-45ff-82b3-24c9d81d4e32": // Easy Going
            if (screenId) {
                // Keep in mind horeca is landscape only
                // We can implement a dropdown on the Screens page to select a stylesheet: Light Cannabis, Dark Cannabis, Horeca etc...
                return orientation === "portrait" ? easyGoingStyleMultiScreen : easyGoingStyleHoreca;
            }
            return orientation === "portrait" ? easyGoingStyleVertical : easyGoingStyle;

        case "470f3f5a-e7b9-43a5-bbfd-2ffce8c161e4": // De Steeg
            if (screenId) return deSteegStyleMultiScreen(extraConfig);
            return orientation === "portrait" ? deSteegStyleVertical : deSteegStyle;

        case "f155b35a-14a1-4866-867f-93fe21e9769b": // Roundabout 69
            if (screenId) return roundAbout69StyleLightMultiScreen;
            return pathname.includes("light") ? roundabout69StyleLight : roundabout69Style;

        case "6adf6283-564c-4afe-b04c-2b6e72300189": //Slowmotion
            return slowMotionStyle;

        default: // Mississippi fallback
            if (screenId) return mississippiStyleMultiScreen;
            return orientation === "portrait" ? mississippiStyleVertical : mississippiStyle;
    }
};

export const useFindQueryParam = (key: string) => {
    return useMemo(() => {
        const params = new URLSearchParams(window.location.search);
        return params.get(key) ?? undefined;
    }, [key]);
};

export const getPricePerGram = (price: Price, includeJoints = false, sortDescending = false): number => {
    const VERY_HIGH_PRICE = 1000000;
    const JOINT_MULTIPLIER = 2; // Assuming 1g is double the price of a joint
    if (!price.json_prices || price.json_prices.length === 0) return Infinity;

    const normalizeLabel = (label?: string) => label?.toLowerCase() ?? "";

    const priorities = ["g", "j", "st"];

    const mapPrice = (jp: { label: string; price: number; quantity: number }, includeJoints: boolean) => {
        const unitPrice = jp.price / jp.quantity;
        if (jp.label.includes("g")) {
            return unitPrice;
        }
        if (sortDescending) {
            if (includeJoints) {
                return unitPrice * JOINT_MULTIPLIER;
            }
            return (unitPrice - VERY_HIGH_PRICE) * JOINT_MULTIPLIER;
        } else {
            if (includeJoints) {
                return unitPrice * JOINT_MULTIPLIER;
            }
            return (VERY_HIGH_PRICE + unitPrice) * JOINT_MULTIPLIER;
        }
    };

    for (const keyword of priorities) {
        const prices = price.json_prices
            .filter((jp) => normalizeLabel(jp.label).includes(keyword) && jp.quantity && jp.quantity > 0)
            .map((jp) => mapPrice(jp, includeJoints));

        if (prices.length > 0) {
            return Math.min(...prices);
        }
    }

    return VERY_HIGH_PRICE;
};

export const getSortingForPrice = (
    priceA: Price,
    priceB: Price,
    includeJoints: boolean = false,
    sortDescending: boolean = false
): number => {
    const pricePerGramA = getPricePerGram(priceA, includeJoints, sortDescending);
    const pricePerGramB = getPricePerGram(priceB, includeJoints, sortDescending);

    if (pricePerGramA === pricePerGramB) {
        return 0;
    }

    if (sortDescending) {
        return pricePerGramA > pricePerGramB ? -1 : 1;
    } else {
        return pricePerGramA < pricePerGramB ? -1 : 1;
    }
};
